import './domain-guard';
import "./_globals";
import '../css/icons.css';
import '../css/grid.css';
import '../css/default.css';
import '../css/custom-check-radio.css';
import '../css/check-radio.css';
import '../css/shared/dark-mode.css';
import '../css/button.css';
import '../css/fancy-title.css';
import '../css/dialog.css';

import '@here/maps-api-for-javascript/bin/mapsjs-ui.css';

import EventEmitter from "eventemitter3";

import { Context } from './models/context';
import { Data } from './models/data';
import { Order } from './models/order';
import { User } from './models/user';

import { ITracker, IUnsubscribeOptions } from './models/context.interface';

import { ProfileService } from './services/profile-service';
import { createEventBus, EventTypes } from './core/event-bus';
import { LoyaltyProviderFactory } from './services/loyalty-provider/loyalty-provider-factory';
import { LoyaltyService } from './services/loyalty-service';
import { OrderService } from './services/order-service';
import { TrackerService } from "./services/tracker-service";

import { Account } from "./components/pages/Account/account";
import { Favorites } from "./components/pages/Favorites/favorites";
import { Landing } from "./components/pages/Landing/landing";
import { Locations } from "./components/pages/Locations/locations";
import { Menu } from "./components/pages/Menu/menu";
import { MenuViewAll } from "./components/pages/MenuViewAll/menu-view-all";
import { Orders } from "./components/pages/Orders/orders";
import { Rewards } from "./components/pages/Rewards/rewards";
import { Terms } from "./components/pages/Terms/terms";
import { TrackOrder } from "./components/pages/TrackOrder/track-order";
import { Curbside } from "./components/pages/Curbside/curbside";
import { ICurbsideOptions } from './components/pages/Curbside/curbside.interface';
import { Unsubscribe } from "./components/pages/Unsubscribe/unsubscribe";
import { LocationService } from './services/location-service';
import { CouponService } from './services/coupons/coupon-service';
import { DataLayerService } from './services/data-layer-service';

import { Router } from "./core/router"
import { Pages, Modals, OrderType, CookiePreferences } from './types/enums';
import { Header } from './components/shared/Header/header';
import { NavigationBar } from './components/shared/NavigationBar/navigation-bar';
import { DealBuilder } from './components/modals/DealBuilder/deal-builder';
import { Login } from './components/modals/Login/login';
import { Signup } from './components/modals/Signup/signup';
import { Util } from './utils/util';
import { ErrorListener } from './utils/error-listener';
import { Cart } from './components/pages/Cart/cart';
import { OnlineOrderingService } from './services/online-ordering-service';
import { BackToHome } from './components/shared/BackToHome/back-to-home';
import { Checkout } from './components/pages/Checkout/checkout';
import { Footer } from './components/shared/Footer/footer';
import { OrderTypeAddressNavBar } from './components/shared/OrderTypeAddressNavBar/order-type-address-nav-bar';
import { Confirmation } from './components/modals/Confirmation/confirmation';
import { AccountContact } from './components/modals/Account/account-contact';
import { AccountChangePassword, IResetPasswordProps } from './components/modals/Account/account-change-password';
import { AddressService } from './services/address-service';
import { AccountConfirmDelete } from './components/modals/Account/account-confirm-delete';
import { LocationSelector } from './components/modals/LocationSelector/location-selector';
import { Fundraiser } from './components/modals/Fundraiser/fundraiser';
import { Hamburger } from './components/modals/Hamburger/hamburger';
import { OrderTypeAndTime } from './components/modals/OrderTypeAndTime/order-type-and-time';
import { HeaderAccount } from './components/modals/HeaderAccount/header-account';
import { Challenges } from './components/modals/Challenges/challenges';
import { LocalizationService } from './services/localization-service';
import { initGeoSuggestionAnnouncements } from './utils/suggestions-announcer';
import { GeolocationService } from './services/geolocation-service';
import { CookiePreference } from './components/modals/CookiePreference/cookie-preference';
import { ILoyaltyProvider } from './services/loyalty-provider/loyalty-provider.interface';
import { BrandService } from './services/brand-service';
import { HttpService } from './services/http-service';
import { CustomizeItemController } from './components/modals/CustomizeItem/controller/customize-item-controller';
import { AppBanner } from './components/shared/Header/app-banner';
import { LoaderSpinner } from './components/shared/LoaderSpinner/loader-spinner';
import { DialogCreators } from './utils/dialog-creators';
import { BannerService } from './services/banner-service';
import { SelectSecondHalf } from './components/modals/CustomizeItem/ui/multi-item/modals/select-second-half/select-second-half';
import { RewardGroupItems } from "./components/modals/RewardGroupItems/reward-group-items";
import { StoreSelector } from "./components/modals/StoreSelector/store-selector";
import { StoreSelectorUtil } from "./components/modals/StoreSelector/store-selector-util";
import { IGlobalVar } from './interfaces/global-var.interfaces';

export async function initiateOLOPage(globalvar: IGlobalVar, languageCode: string): Promise<void> {
    // Captured once, here, before the SPA router's first navigation strips the query string --
    // Router._navigate only preserves window.location.search when routing to Landing/Menu; every
    // other page (including Checkout) rebuilds the URL as just origin+path. Persisted to
    // sessionStorage under its own key (not localStorage["appView"], which has its own unrelated
    // history) so pages reached after navigating away can still read the original Mobile value.
    // Only written when the param is actually present on this request -- a request that omits it
    // (e.g. this bootstrap re-running after an external redirect back into the SPA) is not proof
    // the session isn't the wrapped app; it just carries no signal, so any previously-captured
    // value in this session is left alone rather than being clobbered.
    const initialMobileParam = Util.getQueryParamCaseInsensitive(window.location.search, "Mobile");
    if (initialMobileParam !== null)
        sessionStorage.setItem("mobileAppRequest", initialMobileParam.toLowerCase());

    const eventBus = createEventBus();

    globalvar.languageCode = LocalizationService.normalizeLanguageCode(languageCode);

    // Set isApp flag
    let isAppView: boolean = false;

    if (globalvar.isApp === null)
        isAppView = Util.isAppView();
    else
        isAppView = globalvar.isApp;

    const bodyElement = document.body;

    if (isAppView) {
        localStorage.setItem("appView", "true");
        bodyElement.classList.add("mobile");
        await getMobileLocationAsync();
    } else {
        localStorage.removeItem("appView");
        bodyElement.classList.remove("mobile");
        await Util.getDesktopLocationAsync();
    }

    const context = new Context(globalvar);
    if (context.getProperty("developmentMode"))
        console.log(`useCouponService app setting set as ${context.getProperty("useCouponService")}`);

    // API Endpoints
    const apiVersion = "1.0";
    const apiLoyalty = globalvar.LoyaltyApiUrl;
    const apiProfile = `${location.origin}/api/v${apiVersion}/profile/`;
    const apiTracker = `${location.origin}/api/v${apiVersion}/tracker/`;
    const apiLocation = `${location.origin}/api/v${apiVersion}/locations/`;
    const apiAddress = globalvar.AddressLookupUrl;
    const apiOrder = `${location.origin}/api/v${apiVersion}/order/`;
    const apiOnlineOrdering = `${location.origin}/api/v${apiVersion}/olo/`;
    const apiGeolocation = `${location.origin}/api/v${apiVersion}/geo/`;
    const apiBrand = `${location.origin}/api/v${apiVersion}/brand/`;
    const publicKey = globalvar.pkey;

    const httpService = new HttpService();

    // Models
    const data = new Data(eventBus, httpService, context, apiOnlineOrdering, globalvar.locations, globalvar.loyaltyInfo);

    const localizationService = new LocalizationService(context);
    await localizationService.loadAndMergeTranslationsAsync();

    const user = new User(eventBus);
    const dataLayerService = new DataLayerService(data, context, localizationService);

    // 2DO: need to invert this model/service relationship
    const onlineOrderingService = new OnlineOrderingService(httpService, data, apiAddress, apiOrder, apiOnlineOrdering, publicKey);
    const couponService = new CouponService(eventBus, data, context, user, onlineOrderingService, localizationService, dataLayerService);
    const order = new Order(eventBus, data, context, localizationService, user, dataLayerService, onlineOrderingService, couponService);

    // Services
    const loyaltyService = new LoyaltyService(httpService, data, apiLoyalty, publicKey);
    const loyaltyProvider = LoyaltyProviderFactory.getLoyaltyProvider(data, context, localizationService, user, order, loyaltyService, dataLayerService, globalvar.loyaltyInfo.ProviderId);
    const profileService = new ProfileService(httpService, eventBus, data, context, user, order, loyaltyProvider, apiProfile, apiLoyalty, publicKey);
    const locationService = new LocationService(httpService, data, apiLocation);
    const addressService = new AddressService(httpService, data, apiAddress);
    const trackerService = new TrackerService(httpService, data, apiTracker);
    const orderService = new OrderService(httpService, data, order, apiOrder, apiOnlineOrdering, publicKey);
    const geolocationService = new GeolocationService(httpService, data, apiGeolocation);
    const brandService = new BrandService(httpService, data, apiBrand, publicKey);
    const bannerService = new BannerService(data, profileService, context);

    // Event Bus Listeners run in they order of their subscription
    // These will be run before any page listeners
    setEventBusListeners(eventBus, context, data, order, localizationService, profileService, loyaltyProvider);

    // Components
    const header = new Header(eventBus, data, context, localizationService, orderService, profileService, locationService);
    header.init();
    const navbar = new NavigationBar(eventBus, data);
    navbar.init();
    const backHome = new BackToHome(eventBus, data);
    backHome.init();
    const footer = new Footer(context, dataLayerService);
    footer.init();
    const cookiePopup = new CookiePreference(eventBus, data, context, localizationService, dataLayerService);
    cookiePopup.init();

    const orderTypeAddressBar = new OrderTypeAddressNavBar(eventBus, data, localizationService, orderService);

    const errorListener = new ErrorListener(dataLayerService);
    errorListener.init();

    const locationSelector = new LocationSelector(eventBus, data, context, localizationService, geolocationService, dataLayerService);
    const storeSelector = new StoreSelector(eventBus, data, context, localizationService, dataLayerService, locationSelector);
    StoreSelectorUtil.init(data, localizationService, context);
    initGeoSuggestionAnnouncements(() => localizationService.names("AutoSuggestionsAvailable"));

    const customizeItem = new CustomizeItemController({
        eventBus,
        data,
        order,
        context,
        localizationService,
        dataLayerService,
        profileService
    });

    const router = new Router(
        eventBus,
        {
            [Pages.ACCOUNT]: new Account(eventBus, data, context, localizationService, user, profileService, order, dataLayerService),
            [Pages.FAVORITES]: new Favorites(eventBus, data, context, localizationService, profileService, orderService, dataLayerService),
            [Pages.LANDING]: new Landing(eventBus, data, context, localizationService, orderService, profileService, locationService, dataLayerService, bannerService),
            [Pages.LOCATIONS]: new Locations(eventBus, data, context, localizationService, profileService, orderService, locationService, addressService, dataLayerService),
            [Pages.MENU]: new Menu(eventBus, data, context, localizationService, profileService, orderService, dataLayerService, loyaltyProvider),
            [Pages.MENU_VIEW_ALL]: new MenuViewAll(eventBus, data, context, localizationService, order, dataLayerService, loyaltyProvider, profileService),
            [Pages.ORDERS]: new Orders(eventBus, data, context, localizationService, profileService, orderService, dataLayerService),
            [Pages.REWARDS]: new Rewards(eventBus, data, context, localizationService, profileService, dataLayerService, order),
            [Pages.TERMS]: new Terms(eventBus, data, context, localizationService),
            [Pages.TRACK_ORDER]: new TrackOrder(eventBus, data, context, localizationService, order, user, trackerService, dataLayerService),
            [Pages.CART]: new Cart(eventBus, data, context, localizationService, order, dataLayerService, loyaltyProvider, profileService),
            [Pages.CHECKOUT]: new Checkout(eventBus, data, context, localizationService, order, user, dataLayerService, loyaltyProvider, onlineOrderingService, profileService),
            [Pages.CURBSIDE]: new Curbside(eventBus, data, context, localizationService, brandService, dataLayerService),
            [Pages.UNSUBSCRIBE]: new Unsubscribe(eventBus, data, context, localizationService, profileService, globalvar.unsubscribe)
        },
        {
            [Modals.SIGNUP]: new Signup(eventBus, data, context, localizationService, profileService, dataLayerService, storeSelector),
            [Modals.LOGIN]: new Login(eventBus, context, localizationService, profileService, dataLayerService, data),
            [Modals.HAMBURGER]: new Hamburger(eventBus, data, context, localizationService, profileService, dataLayerService),
            [Modals.CONFIRMATION]: new Confirmation(eventBus, data, context, localizationService, order, user, dataLayerService, loyaltyProvider, profileService),
            [Modals.MEAL_DEAL]: new DealBuilder(eventBus, data, context, localizationService, order, dataLayerService, loyaltyProvider, profileService),
            [Modals.ACCOUNT_CONTACT]: new AccountContact(eventBus, data, context, localizationService, profileService, dataLayerService),
            [Modals.ACCOUNT_CHANGE_PASSWORD]: new AccountChangePassword(eventBus, data, context, localizationService, profileService, dataLayerService),
            [Modals.ACCOUNT_CONFIRM_DELETE]: new AccountConfirmDelete(eventBus, data, context, localizationService, profileService, dataLayerService),
            [Modals.LOCATION_SELECTOR]: () => locationSelector,
            [Modals.FUNDRAISER]: new Fundraiser(eventBus, context, dataLayerService, localizationService, order),
            [Modals.HEADER_ACCOUNT]: new HeaderAccount(eventBus, data, context, dataLayerService, localizationService, profileService),
            [Modals.CHALLENGES]: new Challenges(eventBus, data, context, localizationService, orderService, dataLayerService),
            [Modals.CUSTOMIZE_ITEM]: () => customizeItem.ui,
            [Modals.SELECT_SECOND_HALF]: new SelectSecondHalf(eventBus, data, order, context, localizationService, dataLayerService),
            [Modals.REWARD_GROUP_ITEMS]: new RewardGroupItems(eventBus, data, order, context, localizationService, dataLayerService),
            [Modals.STORE_SELECTOR]: () => storeSelector,
            [Modals.ORDER_TYPE_AND_TIME]: new OrderTypeAndTime(eventBus, data, context, localizationService, order, dataLayerService, loyaltyProvider, onlineOrderingService, profileService, locationService, addressService)
        }
    );
    router.init();

    handleAuthRequiredLandingPage(eventBus, globalvar.landingPage);
    await data.loadBrandDataAsync();
    await order.loadCartFromLocalStorageAsync(loyaltyProvider);
    emitEvents(eventBus, context, loyaltyProvider, !!globalvar.landingPage);
    handleNoAuthLandingPage(eventBus, globalvar.landingPage);
    handleModalOpen(eventBus, globalvar.landingPage, profileService);
    handleResetPassword(eventBus, globalvar.resetPassword);
    handleCurbside(eventBus, globalvar.curbside);
    handleUnsubscribe(eventBus, globalvar.unsubscribe);
    handleTracker(eventBus, globalvar.tracker);
    addBrowserRefreshListener(orderService, data);
    await loadEssentialScripts(eventBus, context, data);
    checkAppVersion(globalvar.isAppUpdateRequired, localizationService);
}

export function setEventBusListeners(eventBus: EventEmitter, context: Context, data: Data, order: Order, localizationService: LocalizationService, profileService: ProfileService, loyaltyProvider: ILoyaltyProvider): void {
    eventBus.on(EventTypes.LANGUAGE_CHANGED, (language: string, save?: boolean) => {
        language = language?.replace(/\s/g, '')

        if (!language)
            return;

        context.setProperty("languageCode", language);
        localizationService.setPageLanguage();

        if (profileService.user.isLoggedIn() && save) {
            profileService.user.setProperty("CultureCode", language)
            profileService.saveProfile(profileService.user.user, () => { }, false);
        }
    })

    eventBus.on(EventTypes.BRAND_DATA_RETRIEVED, async () => {
        const targetStoreKey = context.getProperty("targetStoreKey");
        const isSignedIn = await trySignUserInAsync(eventBus, context, profileService);

        if (targetStoreKey) {
            if (isSignedIn)
                setTimeout(() => eventBus.emit(EventTypes.MODAL_OPEN, Modals.ORDER_TYPE_AND_TIME), 0);
            else
                setTimeout(() => eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN, { externalRedirect: true }), 0);
        }

        handleAppBanner(eventBus, data, context, localizationService);
    });

    eventBus.on(EventTypes.DATA_RETRIEVED, () => {
        if (profileService.user.getProperty("CultureCode")) {
            eventBus.emit(EventTypes.LANGUAGE_CHANGED, LocalizationService.normalizeLanguageCode(profileService.user.getProperty("CultureCode") || context.getProperty("languageCode")));
        }
    });

    eventBus.on(
        EventTypes.LOGGED_IN,
        () => {
            if (!data.storeKey && profileService.user.getProperty("DefaultStore") && order.getProperty("OrderTypeID") !== OrderType.DELIVERY) {
                eventBus.emit(EventTypes.STORE_CHANGED, profileService.user.getProperty("DefaultStore")?.StoreKey ?? "", loyaltyProvider);
            }

            if (profileService.user.getProperty("CultureCode")) {
                const language = profileService.user.getProperty("CultureCode");
                if (language) eventBus.emit(EventTypes.LANGUAGE_CHANGED, LocalizationService.normalizeLanguageCode(language))
            }

            setInterval(async () => {
                if (profileService.user.isLoggedIn()) await profileService.refreshToken();
            }, 10 * 60 * 1000)
        });
    }

function emitEvents(eventBus: EventEmitter, context: Context, loyaltyProvider: ILoyaltyProvider, landingPageIsSet: boolean): void {
    eventBus.emit(EventTypes.ORDER_TYPE_CHANGED, context.getProperty("defaultOrderType"));
    loyaltyProvider.setPrograms();

    const storeKey = context.getProperty("targetStoreKey");
    if (storeKey) {
        eventBus.emit(EventTypes.STORE_CHANGED, storeKey, loyaltyProvider);

        if (!landingPageIsSet)
            eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.MENU);

        context.setProperty("targetStoreKey", '');
    } else {
        const currentPath = (location.pathname as Pages);
        const excludePages = [Pages.CHECKOUT, Pages.MENU_VIEW_ALL, Pages.UNSUBSCRIBE, Pages.CURBSIDE, Pages.TRACK_ORDER];
        const page = excludePages.includes(currentPath) ? Pages.LANDING : currentPath;
        if (!landingPageIsSet)
            eventBus.emit(EventTypes.LOCATION_CHANGE, page || Pages.LANDING)
    }

    eventBus.emit(EventTypes.LANGUAGE_CHANGED, context.getProperty("languageCode"))
}

function handleResetPassword(eventBus: EventEmitter, resetProp: IResetPasswordProps | undefined) {
    if (resetProp?.profileIdEncrypted) {
        eventBus.emit(EventTypes.MODAL_OPEN, Modals.ACCOUNT_CHANGE_PASSWORD, resetProp);
    }
}

function handleCurbside(eventBus: EventEmitter, curbsideProp?: ICurbsideOptions) {
    if (curbsideProp) {
        eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.CURBSIDE, curbsideProp);
    }
}

function handleUnsubscribe(eventBus: EventEmitter, unsubscribeProp?: IUnsubscribeOptions) {
    if (unsubscribeProp)
        eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.UNSUBSCRIBE);
}

/**
 * Creates Listeners to logging events and redirects accordingly.
 * @param {EventEmitter} eventBus - The global event bus to emit events on.
 * @param {Pages} [page] - Optional page to handle authentication for.
 */
export function handleAuthRequiredLandingPage(eventBus: EventEmitter, page?: Pages) {
    if (!page) return;

    eventBus.once(EventTypes.BRAND_DATA_RETRIEVED, () => {
        eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LANDING);
    });

    if (!isAuthRequired(page)) return;

    const loader = new LoaderSpinner();

    eventBus.once(EventTypes.LOGGED_OUT, () => {
        eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN);
        loader.destroy();
    });

    eventBus.once(EventTypes.LOGGED_IN, () => {
        eventBus.emit(EventTypes.LOCATION_CHANGE, page);
        loader.destroy();
    });
}

export function isAuthRequired(page: Pages | undefined): boolean {
    if (!page) return false;

    const authPages = [Pages.ACCOUNT, Pages.REWARDS, Pages.ORDERS];
    return authPages.includes(page);
}

export function handleNoAuthLandingPage(eventBus: EventEmitter, page?: Pages): void {
    if (!page) return;
    if (isAuthRequired(page)) return;
    if (isModalLandingPage(page)) return;

    eventBus.emit(EventTypes.LOCATION_CHANGE, page);
}

function isModalLandingPage(page: Pages): boolean {
    return Object.values(Modals).some(modal => (page as string) === `/${modal}`);
}

export function handleModalOpen(eventBus: EventEmitter, landingPage: Pages | undefined, profileService: ProfileService): void {
    if ((landingPage as string) === `/${Modals.SIGNUP}` && !profileService.user.isLoggedIn())
        eventBus.emit(EventTypes.MODAL_OPEN, Modals.SIGNUP);
}

export async function loadEssentialScripts(eventBus: EventEmitter, context: Context, data: Data): Promise<void> {
    const contentFolderUrl = context.getProperty("publicStorageUrl");
    const gtmId = data.getBrandProperty("Settings").OOS.googleTagManager;
    let essentialUrl = `${contentFolderUrl}/online/brand-essential.js`;
    let loaded = true;

    if (gtmId)
        essentialUrl = `${essentialUrl}?gtmId=${encodeURIComponent(gtmId)}`;

    await Util.loadScriptAsync(essentialUrl)
        .catch((err) => {
            console.error("Essential scripts failed to load:", err);
            loaded = false;
        });

    if (!loaded)
        return;

    let essentialBodyUrl = `${contentFolderUrl}/online/brand-essential-body.js`;
    if (gtmId)
        essentialBodyUrl = `${essentialBodyUrl}?gtmId=${encodeURIComponent(gtmId)}`;
    await Util.loadScriptAsync(essentialBodyUrl)
        .then(() => {
            //@ts-expect-error
            if (globalThis.dataLayer)
                eventBus.emit(EventTypes.DATALAYER_LOADED);

            //@ts-expect-error
            if (globalThis.gtag && Util.getCookiePreferenceFromCookie()) {
                Util.setConsent(Util.getCookiePreferenceFromCookie() === CookiePreferences.ACCEPT);
            }

        })
        .catch((err) => {
            console.error("Essential body scripts failed to load:", err);
        });
}

async function trySignUserInAsync(eventBus: EventEmitter, context: Context, profileService: ProfileService): Promise<boolean> {
    let ok = false;

    const ssoToken = context.getProperty("singleSignOnToken");
    if (ssoToken)
        ok = await profileService.signInSSO(ssoToken);
    else
        ok = await profileService.signInJWT();

    if (ok)
        eventBus.emit(EventTypes.LOGGED_IN);
    else
        eventBus.emit(EventTypes.LOGGED_OUT);

    return ok;
}

/**
 * Emits a LOCATION_CHANGE event when a tracker is provided.
 *
 * @param {EventEmitter} eventBus - The global event bus to emit events on.
 * @param {ITracker} [trackerProp] - Optional tracker object. If present, its OrderId is emitted.
 * @returns {void}
 */
function handleTracker(eventBus: EventEmitter, trackerProp?: ITracker): void {
    if (trackerProp)
        eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.TRACK_ORDER, { orderId: trackerProp.OrderId, fromRecentOrdersPage: false });
}

/**
 * adds a listener for browser refresh that checks if there are coupons in the order and if there is a store key in the data. 
 * If so, it prevents the default behavior of the refresh which is to lose the coupons in the order.
 * @param orderService
 * @param data
 */
function addBrowserRefreshListener(orderService: OrderService, data: Data): void {
    window.addEventListener("beforeunload", event => {
        const coupons = orderService.order.getProperty("Coupons");

        if (!coupons || coupons.length === 0 || !data.storeKey) {
            return;
        }

        event.preventDefault();
        event.returnValue = "";
    });
}

/**
 * Displays the mobile app download banner depending on platform and brand settings.
 *
 * For iOS: dynamically creates or updates the Smart App Banner meta tag.
 * For Android: displays a custom banner with app details and download link.
 *
 * @param {Data} data - The brand data source used to fetch banner configuration.
 * @param {Context} context - Application context providing brand info and public storage URL.
 * @returns {void}
 */
export function handleAppBanner(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService): void {
    if (!Util.isiOSSafari()) {
        const metaTag = document.querySelector<HTMLMetaElement>("meta[name='apple-itunes-app']");
        metaTag?.remove();
    }

    const isMobileView = Util.isAndroid() || Util.isiOSNonSafari();
    if (Util.isAppView() || !data.getBrandProperty("Settings").OOS.showAppBanner || !isMobileView)
        return;

    const header = document.querySelector("header");
    const headerDiv = header?.querySelector(".header") as HTMLElement | undefined;
    const banner = new AppBanner(eventBus, data, context, localizationService);
    if (header && headerDiv) {
        banner.attach(header, headerDiv);
        document.body.classList.add("android-app-banner-visible");
    }
}

export async function getMobileLocationAsync(): Promise<void> {
    const cookieLocation = Util.getUserLocationFromCookie();
    if (cookieLocation)
        return;

    let tryCount = 0;
    return new Promise(resolve => {
        function checkFunction() {
            if (typeof getMobileAppLocation == "function") {
                getMobileAppLocation();
                resolve();
            } else if (tryCount > 10) {
                setMobileLocation(null);
                resolve();
            } else {
                tryCount++;
                setTimeout(checkFunction, 100);
            }
        }

        checkFunction();
    });
}

export function setMobileLocation(locationResult: string | null): void {
    const resolver = pendingLocationResolve;
    pendingLocationResolve = null; // clear immediately to prevent double-resolve

    if (!locationResult || locationResult === "") {
        Util.setLocationCookie(0, 0, -10);
        resolver?.(null);
        return;
    }

    try {
        const location = JSON.parse(locationResult) as { lat: number; lng: number };

        const normalized = { latitude: location.lat, longitude: location.lng };
        Util.setLocationCookie(normalized.latitude, normalized.longitude, 3600);

        resolver?.(normalized);
    } catch {
        // If native sent malformed JSON, treat as failure
        Util.setLocationCookie(0, 0, -10);
        resolver?.(null);
    }
}

/**
 * Called by the native Android app shell once its native Google Pay flow completes.
 * `paymentDataResultJson` is the native Google Pay SDK's PaymentData.toJson() output,
 * which matches the same shape the web Google Pay JS API resolves with
 * (paymentMethodData.tokenizationData.token), or null/empty if the user cancelled
 * or the native flow failed.
 */
export function setNativeGooglePayResult(paymentDataResultJson: string | null): void {
    const resolver = pendingGooglePayResolve;
    pendingGooglePayResolve = null; // clear immediately to prevent double-resolve

    if (!paymentDataResultJson) {
        resolver?.(null);
        return;
    }

    try {
        resolver?.(JSON.parse(paymentDataResultJson));
    } catch {
        // If native sent malformed JSON, treat as failure
        resolver?.(null);
    }
}

export function checkAppVersion(isAppUpdateRequired: boolean, localizationService: LocalizationService): void {
    if (!Util.isAppView()) return;

    if (isAppUpdateRequired) {
        const msg = localizationService.names("AppUpdateRequiredMessage");
        DialogCreators.messageBoxOk(localizationService, null, msg);
    }
}

//Logic within initiatePage OLO2 initialization that may be needed in OLO3

////check for old browser, display warning, and that is it
//if (Util.isIncompatibleBrowser()) {
//    DialogCreators.messageBox(Names("BrowserNotSupported"), aOLO.buttonHoverStyle, []);
//    return;
//}

//// Check if page being loaded from app. Used for Punchh app at the moment.
//if (aOLO.singleSignOnToken) {
//    localStorage.MobileView = "true";
//}

//try { setTimeOffset(aOLO.serverTime.UtcMilliseconds, aOLO.serverTime.StoreUtcOffset); } catch (ex: unknown) {
//    if (ex instanceof Error) Util.LogError("InitiatePage SetTimeOffset", ex, aOLO);
//}
//try { aOLO.mediaMax576 = window.matchMedia("(max-width: 576px)"); } catch (ex: unknown) {
//    if (ex instanceof Error) Util.LogError("InitiatePage mediaMax576", ex, aOLO);
//}
//try { aOLO.mediaMax840 = window.matchMedia("(max-width: 840px)"); } catch (ex: unknown) {
//    if (ex instanceof Error) Util.LogError("InitiatePage mediaMax840", ex, aOLO);
//}
//try { aOLO.mediaMax576.addListener(mediaSizeChanged(true)); } catch (ex: unknown) {
//    if (ex instanceof Error) Util.LogError("InitiatePage MediaSizeChanged", ex, aOLO);
//}
//try { aOLO.mediaMax840.addListener(mediaSizeChanged(false)); } catch (ex: unknown) {
//    if (ex instanceof Error) Util.LogError("InitiatePage MediaSizeChanged", ex, aOLO);
//}
//try { aOLO.scrollbarWidth = Util.getScrollbarWidth(); } catch (ex: unknown) {
//    if (ex instanceof Error) Util.LogError("InitiatePage getScrollbarWidth", ex, aOLO);
//}
//try { storeStatus = checkStoreStatus() } catch (ex: unknown) {
//    if (ex instanceof Error) Util.LogError("InitiatePage CheckStoreStatus", ex, aOLO);
//}

//try {
//    setPageLanguage(languageCode);
//    if (Util.isAppView()) {
//        //@ts-ignore
//        if (typeof getAppUserToken == "function")
//            //@ts-ignore
//            getAppUserToken(JSON.stringify({ loc: "olo", key: aOLO.pkey }));
//        else
//            await startApp(data);
//    } else
//        await startApp(data);
//} catch (ex: unknown) {
//    if (ex instanceof Error)
//        Util.LogError("InitiatePage startApp", ex, aOLO);
//}

//// Curried version of LogErrors
//const curriedLogErrors = (msg: string, error: Error) => Util.LogErrors(msg, error, aOLO)
//// Set curriedLogErrors as the window.onerror handler
//window.onerror = curriedLogErrors as any;

//Logic within online-ordering-old.ts OLO2 that may be needed in OLO3

//function setTimeOffset(serverUtcMilliseconds: number, storeUtcOffset: number): void {
//    let iDate = new Date();
//    let clientUtcMilliseconds = iDate.getTime();
//    let ClientMSDiff = serverUtcMilliseconds - clientUtcMilliseconds;
//    let clientUtcOffset = iDate.getTimezoneOffset();
//    aOLO.Temp.TimeOffset = (clientUtcOffset + storeUtcOffset) * 60000 + ClientMSDiff;
//}

//function mediaSizeChanged(isMobile: boolean): void {
//    let catLinks = document.getElementsByClassName("catLink");
//    if (isMobile ? aOLO.mediaMax576.matches : aOLO.mediaMax840.matches) {
//        for (let i = 0; i < catLinks.length; i++) {
//            catLinks[i].classList.remove("hvr-rectangle-out");
//        }
//    } else {
//        for (let i = 0; i < catLinks.length; i++) {
//            catLinks[i].classList.add("hvr-rectangle-out");
//        }
//    }
//}

//function checkStoreStatus(): boolean {
//    let ReqPeriod = 420;
//    if (aOLO.times.LRT > ReqPeriod) {
//        DialogCreators.messageBoxOk(Names("OLO_General_Error"), aOLO.buttonHoverStyle);
//        return false;
//    }
//    return true;
//}

// 2DO: App Specific Functions

//export { setAppUserToken, setMobileLocation, goBack };

//    if (Util.isAppView())
//        await initializationMobile();
//}

//async function initializationMobile(): Promise<void> {
//    await getMobileLocation();
//    try { await initialLocations(true); } catch (ex: unknown) { }
//    await getAppUserTokenLoop();
//}

//async function getAppUserTokenLoop(): Promise<void> {
//    var tryCount = 0;
//    return new Promise(resolve => {
//        function appUserTokenLoop() {
//            //@ts-ignore
//            if (typeof getAppUserToken == "function") {
//                //@ts-ignore
//                getAppUserToken(JSON.stringify({ loc: "brand", key: aOLO.pkey }));
//                resolve();
//            } else {
//                if (tryCount > 3) {
//                    setAppUserToken('');
//                    resolve();
//                } else {
//                    tryCount++;
//                    setTimeout(() => resolve(appUserTokenLoop()), 100);
//                }
//            }
//        }
//        appUserTokenLoop();
//    });
//}

//export async function setAppUserToken(user: User, token: string, data: Data): Promise<void> {
//    await startApp(data);
//    //await profileReloadApp(null, startApp, token, user);
//}

//export async function startApp(data: Data): Promise<void> {
//    setDocHeight();
//    console.log(Util.adoraLogo());


//    //new LanguageBar();

//    await getServerTime();


//    loadOrderTypes(data.getProperty("OrderTypeSubTypes"));

//    Util.showElement("div_cart_container");

//    if (!Util.isAppView()) {
//        await getFooterAsync();
//        displayFooter();
//    }

//    //await setOrderType(aOLO.Temp.OrderTypeID);
//    //initializeGoogleMap();

//    aOLO.Temp.CheckOrderTimeIntervalIsPaused = false;
//    aOLO.Temp.CheckOrderTimeInterval = window.setInterval(async function () {
//        if (!aOLO.Temp.CheckOrderTimeIntervalIsPaused)
//            await CheckOrderTime(getOrderTypeFunctions);
//    }, 10000);

//    //await initializeGUIElementsAsync();
//    //await setStartupDialogs();
//}

//async function CheckOrderTime(getOrderTypeFunctions: Function): Promise<void> {
//    if (!aOLO.Temp.OrderingStarted) return;

//    const now = Util.NowStore(aOLO.Temp.TimeOffset);
//    const nowMinutes = now.getHours() * 60 + now.getMinutes();
//    let DSTET = Util.GetDate_StartTime_EndTime(now, 0, false, aOLO);
//    if (aOLO.Order.FutureDate) {

//        let busDate = Util.GetBUSDate(Util.DateAdd(aOLO.Order.FutureDate, aOLO.Order.FutureMinute, 'm'), aOLO.Temp.TimeOffset, aOLO.data.Settings.BSM);


//        DSTET = Util.GetDate_StartTime_EndTime(busDate, aOLO.Order.FutureMinute, false, aOLO);
//    }
//    if (!DSTET.IsToday)
//        return;

//    if (DSTET.CloseMinute - nowMinutes <= (aOLO.data.Settings.IWTT ? 0 : aOLO.Order.OrderTypeSubType.WaitTime)) {
//        aOLO.Dialog.OrderType = new OrderTypeDialog(getOrderTypeFunctions(), true, false);
//        DialogCreators.messageBoxOk(Names("WillClose").replace("??", (DSTET.CloseMinute - nowMinutes).toString()) + " " + Names("NoTime").replace("???", OnlineOrderingUtil.GetOrderTypeName(aOLO.Order.OrderTypeID, aOLO.data.OrderTypeSubTypes)), aOLO.buttonHoverStyle);
//    } else if (DSTET.CloseMinute - nowMinutes <= (aOLO.data.Settings.IWTT ? 0 : aOLO.Order.OrderTypeSubType.WaitTime) + 5 && !aOLO.Temp.MeetPromiseTimeWarning) {
//        aOLO.Temp.MeetPromiseTimeWarning = true;
//        DialogCreators.messageBoxOk(Names("WillClose").replace("??", (DSTET.CloseMinute - nowMinutes).toString()) + " " + Names("FinishX").replace("??", (DSTET.CloseMinute - nowMinutes - aOLO.Order.OrderTypeSubType.WaitTime).toString()), aOLO.buttonHoverStyle);
//    } else if (DSTET.EndOrderMinute - nowMinutes <= 0) {
//        aOLO.Dialog.OrderType = new OrderTypeDialog(getOrderTypeFunctions(), true, false);
//        DialogCreators.messageBoxOk(Names("MeetPromiseTimeError"), aOLO.buttonHoverStyle);
//    } else if (DSTET.EndOrderMinute - nowMinutes <= 5 && !aOLO.Temp.MeetPromiseTimeWarning) {
//        aOLO.Temp.MeetPromiseTimeWarning = true;
//        DialogCreators.messageBoxOk(Names("MeetPromiseTimeWarning").replace("??", OnlineOrderingUtil.GetOrderTypeName(aOLO.Order.OrderTypeID, aOLO.data.OrderTypeSubTypes)), aOLO.buttonHoverStyle);
//    } else if (DSTET.IsToday && aOLO.Order.FutureDate && aOLO.Order.FutureMinute <= nowMinutes + aOLO.Order.OrderTypeSubType.WaitTime && aOLO.Order.FutureMinute !== -1) {
//        await OnlineOrderingUtil.resetTimeFulfillmentTimeExpired(aOLO, aOLOModules);
//    }
//}

//function setDocHeight() {
//    document.documentElement.style.setProperty('--vh', `${window.innerHeight / 100}px`);
//};

//async function getServerTime(): Promise<void> {
//    let result = await aOLO.Modules.OnlineOrderingService.getServerTime(aOLO.storeInfo.StoreKey, aOLO);
//    if (result)
//        timeOffsetLoad(result);
//    else
//        DialogCreators.messageBoxOk(Names("ErrorGettingServerDate"), aOLO.buttonHoverStyle);
//}

//function timeOffsetLoad(response: { utcMilliseconds: number, storeUtcOffset: number }): void {
//    setTimeOffset(response.utcMilliseconds + 200, response.storeUtcOffset);
//}

//function loadOrderTypes(orderTypes: IDataOrderTypeSubType[]): void {
//    let hasDelivery = false;
//    let hasTakeOut = false;
//    let hasDineIn = false;

//    let waitTimeDelivery = 0;
//    let waitTimeTakeOut = 0;
//    let waitTimeDineIn = 0;

//    const tMinutes = Util.nowTodayMinute(aOLO.Temp.TimeOffset);

//    for (const ot of orderTypes) {
//        const waitTime = 0; //Refactor: OnlineOrderingUtil.getWaitTimeByOrderType(ot, aOLO);
//        Util.setElementClass("add", `div_order_type_${ot.OrderTypeId}`, `orderTypeRadioWidth${orderTypes.length}`);
//        Util.showElement(`div_order_type_${ot.OrderTypeId}`);
//        const orderTypeNameDiv = document.getElementById(`span_order_type_${ot.OrderTypeId}_name`);
//        if (orderTypeNameDiv) {
//            orderTypeNameDiv.innerHTML = this._localizationService.GetName(ot.Names, aOLO.Temp.languageCode);
//            orderTypeNameDiv.setAttribute("ltagj", Util.toLtagj(ot.Names));
//        }

//        if (!aOLO.data.Settings.ISCC)
//            Util.setElement("innerHTML", `span_order_type_${ot.OrderTypeId}_wait`, `${waitTime} <span ltag="Minutes">${Names("Minutes")}</span>`);
//        else
//            Util.setElement("innerHTML", `span_order_type_${ot.OrderTypeId}_wait`, "");

//        if (ot.OrderTypeId === OrderType.DELIVERY) {
//            if (tMinutes >= ot.StartMinute && tMinutes <= ot.EndMinute) {
//                hasDelivery = true;
//                waitTimeDelivery = waitTime;
//            } else {
//                Util.setElement("innerHTML", `span_order_type_${ot.OrderTypeId}_wait`, Util.formatMinutesToTime(ot.StartMinute) + " - " + Util.formatMinutesToTime(ot.EndMinute));
//                Util.setElement("innerHTML", `span_order_type_${ot.OrderTypeId}_name`, this._localizationService.GetName(ot.Names, aOLO.Temp.languageCode) + " - " + Names("LIMITHOUS"));
//            }
//        }

//        if (ot.OrderTypeId === OrderType.TAKE_OUT) {
//            if (tMinutes >= ot.StartMinute && tMinutes <= ot.EndMinute) {
//                hasTakeOut = true;
//                waitTimeTakeOut = waitTime;
//            } else {
//                Util.setElement("innerHTML", `span_order_type_${ot.OrderTypeId}_wait`, Util.formatMinutesToTime(ot.StartMinute) + " - " + Util.formatMinutesToTime(ot.EndMinute));
//                Util.setElement("innerHTML", `span_order_type_${ot.OrderTypeId}_name`, this._localizationService.GetName(ot.Names, aOLO.Temp.languageCode) + " - " + Names("LIMITHOUS"));
//            }
//        }

//        if (ot.OrderTypeId === OrderType.DINE_IN) {
//            if (tMinutes >= ot.StartMinute && tMinutes <= ot.EndMinute) {
//                hasDineIn = true;
//                waitTimeDineIn = waitTime;
//            } else {
//                Util.setElement("innerHTML", `span_order_type_${ot.OrderTypeId}_wait`, Util.formatMinutesToTime(ot.StartMinute) + " - " + Util.formatMinutesToTime(ot.EndMinute));
//                Util.setElement("innerHTML", `span_order_type_${ot.OrderTypeId}_name`, this._localizationService.GetName(ot.Names, aOLO.Temp.languageCode) + " - " + Names("LIMITHOUS"));
//            }
//        }
//    }

//    if (aOLO.Order) {
//        aOLO.Temp.OrderTypeID = aOLO.Order.OrderTypeID;
//        switch (aOLO.Order.OrderTypeID) {
//            case OrderType.DELIVERY:
//                aOLO.Temp.WaitTime = waitTimeDelivery;
//                Util.setElement("checked", "rdo_order_type_delivery", true);
//                break;
//            case OrderType.TAKE_OUT:
//                aOLO.Temp.WaitTime = waitTimeTakeOut;
//                Util.setElement("checked", "rdo_order_type_take_out", true);
//                break;
//            case OrderType.DINE_IN:
//                aOLO.Temp.WaitTime = waitTimeDineIn;
//                Util.setElement("checked", "rdo_order_type_dine_in", true);
//                break;
//        }
//    } else {
//        if (hasDelivery) {
//            aOLO.Temp.OrderTypeID = OrderType.DELIVERY;
//            aOLO.Temp.WaitTime = waitTimeDelivery;
//            Util.setElement("checked", "rdo_order_type_delivery", true);
//        } else if (hasTakeOut) {
//            aOLO.Temp.OrderTypeID = OrderType.TAKE_OUT;
//            aOLO.Temp.WaitTime = waitTimeTakeOut;
//            Util.setElement("checked", "rdo_order_type_take_out", true);
//        } else if (hasDineIn) {
//            aOLO.Temp.OrderTypeID = OrderType.DINE_IN;
//            aOLO.Temp.WaitTime = waitTimeDineIn;
//            Util.setElement("checked", "rdo_order_type_dine_in", true);
//        }
//    }
//}